import { For, type JSX } from 'solid-js';
import { t } from '../i18n';
import { safeDecode } from '../lib/repo-detail';
import { useRepoReviews } from '../lib/repo-reviews';
import { EmptyState } from './EmptyState';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import type { RepoTabProps } from './RepoOverviewTab';
import { RepoReviewItem } from './RepoReviewItem';
import { Skeleton } from './Skeleton';

// Repo detail → Reviews (arc 101 slice 06): every AI code review run on this
// repository, newest-first, with what the reviewer actually had to work with.
//
// CONTAINER. It owns the one read (`useRepoReviews`) and delegates every row to
// `<RepoReviewItem/>`.
//
// A FIXED HEAD, SAID OUT LOUD. `review.list` publishes no page size and no cursor
// — the store owns one window and any query parameter is a 400 — so the footer
// states that this is the newest runs rather than the whole history. Every top
// repo is already past that window (2,040 `pr_review` tasks on this repo alone,
// lifetime, read on prod 2026-09-12), and a list that truncates silently is how a
// reader concludes a review never happened.

export function RepoReviewsTab(props: RepoTabProps): JSX.Element {
  const reviews = useRepoReviews(() => props.encoded, props.token);
  const fullName = (): string => safeDecode(props.encoded);

  return (
    <Panel
      title={t('repo_detail.reviews_title')}
      footer={t('repo_detail.reviews_truncated')}
      class="repo-reviews"
    >
      <p class="repo-reviews__subtitle">{t('repo_detail.reviews_subtitle')}</p>
      <QueryBoundary
        query={reviews}
        isEmpty={(rows) => rows.length === 0}
        loading={<Skeleton height="10rem" />}
        empty={<EmptyState message={t('repo_detail.reviews_empty')} />}
      >
        {(rows) => (
          <ul class="repo-reviews__list">
            <For each={rows}>{(run) => <RepoReviewItem run={run} fullName={fullName()} />}</For>
          </ul>
        )}
      </QueryBoundary>
    </Panel>
  );
}
